import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { InfobipRcsIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getDocsUrl } from '@/util/constants';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';

interface Props {
  integration: InfobipRcsIntegration;
}

/**
 * InfobipRcs integration connection setup component
 *
 * Provides an editable form for users to input the required connection details:
 * - API Key
 * - Base URL
 * - Sender Name
 *
 * This component is shown when the integration is not yet connected.
 */
export function InfobipRcsConnect({ integration }: Props) {
  const { t } = useTranslation();
  const { updateIntegration, updateStatus } = useIntegrations(
    integration.desk_id,
    integration.integration_id
  );

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      apikey: integration.config.apikey ?? '',
      base_url: integration.config.base_url ?? '',
      sender_name: integration.config.sender_name ?? '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = formMethods;

  const onSubmit = (form: Record<string, string>) => {
    updateIntegration({ ...integration, config: { ...integration.config, ...form } });
  };

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  return (
    <FormFieldset
      title={t('integrations.infobip_rcs_connect.title')}
      subtitle={t('integrations.infobip_rcs_connect.subtitle')}
      primaryButton={{
        children: t('common.save'),
        isLoading: updateStatus === 'pending',
        disabled: !isDirty,
      }}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={updateStatus === 'pending'}
      helpText={t('integrations.infobip_rcs_connect.footer_text')}
      externalLink={{
        url: getDocsUrl('/docs/integrations/infobip-rcs'),
        label: t('common.learn_more'),
      }}
    >
      <Form {...formMethods}>
        <div className="space-y-6">
          <FormField
            control={control}
            name="apikey"
            rules={{ required: t('common.required_field') }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('integrations.infobip_rcs_connect.apikey.title')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onBlur={trimOnBlur(field)}
                    readOnly={!canWrite}
                    placeholder={t('integrations.infobip_rcs_connect.apikey.placeholder')}
                    autoComplete="off"
                  />
                </FormControl>
                <FormMessage>{errorMessage({ field: errors.apikey })}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="base_url"
            rules={{ required: t('common.required_field') }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('integrations.infobip_rcs_connect.base_url.title')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onBlur={trimOnBlur(field)}
                    readOnly={!canWrite}
                    placeholder={t('integrations.infobip_rcs_connect.base_url.placeholder')}
                  />
                </FormControl>
                <FormMessage>{errorMessage({ field: errors.base_url })}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="sender_name"
            rules={{ required: t('common.required_field') }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('integrations.infobip_rcs_connect.sender_name.title')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onBlur={trimOnBlur(field)}
                    readOnly={!canWrite}
                    placeholder={t('integrations.infobip_rcs_connect.sender_name.placeholder')}
                  />
                </FormControl>
                <FormMessage>{errorMessage({ field: errors.sender_name })}</FormMessage>
              </FormItem>
            )}
          />
        </div>
      </Form>
    </FormFieldset>
  );
}

export default InfobipRcsConnect;
